import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  credentialsApi,
  ProjectCredential,
  CreateCredentialRequest,
} from '@/services/credentialsService';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Eye,
  EyeOff,
  Key,
  Loader2,
  Copy,
  Check,
  Save,
  Link,
  User,
  Lock,
  FileText,
} from 'lucide-react';

interface ProjectCredentialsTabProps {
  projectId: string;
}

interface FormData {
  permitSubmittalLink: string;
  username: string;
  password: string;
  notes: string;
}

const ProjectCredentialsTab: React.FC<ProjectCredentialsTabProps> = ({ projectId }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State management
  const [formData, setFormData] = useState<FormData>({
    permitSubmittalLink: '',
    username: '',
    password: '',
    notes: '',
  });

  const [revealedUsername, setRevealedUsername] = useState<string>('');
  const [revealedPassword, setRevealedPassword] = useState<string>('');
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isRevealingUsername, setIsRevealingUsername] = useState(false);
  const [isRevealingPassword, setIsRevealingPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'Admin';

  // Queries
  const {
    data: credentialsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['project-credentials', projectId],
    queryFn: () => credentialsApi.getProjectCredentials(projectId),
    enabled: isAdmin && !!projectId,
  });

  // Initialize form data when credentials are loaded
  useEffect(() => {
    if (credentialsResponse?.data) {
      const credential = credentialsResponse.data;
      setFormData({
        permitSubmittalLink: credential.permitSubmittalLink || '',
        username: '', // Don't show encrypted username
        password: '', // Don't show encrypted password
        notes: credential.notes || '',
      });
    }
  }, [credentialsResponse]);

  // Track changes
  useEffect(() => {
    const originalData = credentialsResponse?.data;
    if (originalData) {
      const hasFormChanges = 
        formData.permitSubmittalLink !== (originalData.permitSubmittalLink || '') ||
        formData.username.trim() !== '' ||
        formData.password.trim() !== '' ||
        formData.notes !== (originalData.notes || '');
      setHasChanges(hasFormChanges);
    } else {
      // No existing data, check if any field has content
      const hasFormChanges = 
        formData.permitSubmittalLink.trim() !== '' ||
        formData.username.trim() !== '' ||
        formData.password.trim() !== '' ||
        formData.notes.trim() !== '';
      setHasChanges(hasFormChanges);
    }
  }, [formData, credentialsResponse]);

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (data: CreateCredentialRequest) =>
      credentialsApi.createOrUpdateCredential(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-credentials', projectId] });
      setHasChanges(false);
      // Clear password field after successful save
      setFormData(prev => ({ ...prev, username: '', password: '' }));
      toast({
        title: 'Success',
        description: 'Credentials saved successfully',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save credentials',
        variant: 'destructive',
      });
    },
  });

  // Helper functions
  const handleSave = () => {
    const dataToSave: CreateCredentialRequest = {};

    if (formData.permitSubmittalLink.trim()) {
      dataToSave.permitSubmittalLink = formData.permitSubmittalLink.trim();
    }
    if (formData.username.trim()) {
      dataToSave.username = formData.username.trim();
    }
    if (formData.password.trim()) {
      dataToSave.password = formData.password;
    }
    if (formData.notes.trim()) {
      dataToSave.notes = formData.notes.trim();
    }

    // Only save if there's actually data to save
    if (Object.keys(dataToSave).length > 0) {
      saveMutation.mutate(dataToSave);
    } else {
      toast({
        title: 'No Data',
        description: 'Please enter at least one field to save',
        variant: 'destructive',
      });
    }
  };

  const handleRevealUsername = async () => {
    if (showUsername) {
      setShowUsername(false);
      setRevealedUsername('');
      return;
    }

    setIsRevealingUsername(true);
    try {
      const response = await credentialsApi.revealCredentialUsername(projectId);
      setRevealedUsername(response.data.username || '');
      setShowUsername(true);
    } catch (error: unknown) {
      const err = error as Error & { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to reveal username',
        variant: 'destructive',
      });
    } finally {
      setIsRevealingUsername(false);
    }
  };

  const handleRevealPassword = async () => {
    if (showPassword) {
      setShowPassword(false);
      setRevealedPassword('');
      return;
    }

    setIsRevealingPassword(true);
    try {
      const response = await credentialsApi.revealCredentialPassword(projectId);
      setRevealedPassword(response.data.password || '');
      setShowPassword(true);
    } catch (error: unknown) {
      const err = error as Error & { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to reveal password',
        variant: 'destructive',
      });
    } finally {
      setIsRevealingPassword(false);
    }
  };

  const handleCopyValue = (value: string, fieldName: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
    toast({
      title: 'Copied',
      description: `${fieldName} copied to clipboard`,
    });
  };

  // Don't render if user is not admin
  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">
              Only administrators can view and manage project credentials.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading credentials...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2 text-destructive">Error</h3>
            <p className="text-muted-foreground">Failed to load credentials</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-sophisticated border-none">
        <CardHeader className="border-b border-border/50 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                <Key className="h-5 w-5 text-primary" />
                Project Credentials
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Manage permit links, login credentials, and notes for this project
              </p>
            </div>
            {hasChanges && (
              <Button onClick={handleSave} disabled={saveMutation.isPending} className="shadow-sm">
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Permit Submittal Link */}
          <div className="space-y-2">
            <Label htmlFor="permitLink" className="flex items-center gap-2">
              <Link className="h-4 w-4" />
              Permit Submittal Link
            </Label>
            <Input
              id="permitLink"
              type="url"
              value={formData.permitSubmittalLink}
              onChange={(e) => setFormData({ ...formData, permitSubmittalLink: e.target.value })}
              placeholder="https://permits.example.com/submit"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              URL where permits are submitted for this project
            </p>
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Username
            </Label>
            <div className="flex gap-2">
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter new username"
                className="font-mono"
              />
              {credentialsResponse?.data && (
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRevealUsername}
                    disabled={isRevealingUsername}
                  >
                    {isRevealingUsername ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : showUsername ? (
                      <EyeOff className="h-3 w-3" />
                    ) : (
                      <Eye className="h-3 w-3" />
                    )}
                  </Button>
                  {showUsername && revealedUsername && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyValue(revealedUsername, 'Username')}
                    >
                      {copiedField === 'Username' ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
            {showUsername && revealedUsername && (
              <div className="mt-2 p-2 bg-muted rounded text-sm font-mono">
                Current: {revealedUsername}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Login username (encrypted when saved)
            </p>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Password
            </Label>
            <div className="flex gap-2">
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter new password"
                className="font-mono"
              />
              {credentialsResponse?.data && (
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRevealPassword}
                    disabled={isRevealingPassword}
                  >
                    {isRevealingPassword ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : showPassword ? (
                      <EyeOff className="h-3 w-3" />
                    ) : (
                      <Eye className="h-3 w-3" />
                    )}
                  </Button>
                  {showPassword && revealedPassword && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyValue(revealedPassword, 'Password')}
                    >
                      {copiedField === 'Password' ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
            {showPassword && revealedPassword && (
              <div className="mt-2 p-2 bg-muted rounded text-sm font-mono">
                Current: {revealedPassword}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Login password (encrypted when saved)
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notes
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any relevant notes, instructions, or contact information..."
              rows={4}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground">
              Additional information visible to all admins ({formData.notes.length}/2000)
            </p>
          </div>

          {/* Save Button (also appears at bottom) */}
          {hasChanges && (
            <div className="pt-4 border-t">
              <Button onClick={handleSave} disabled={saveMutation.isPending} className="w-full shadow-sm">
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          )}

          {/* Last Updated Info */}
          {credentialsResponse?.data && (
            <div className="pt-4 border-t text-sm text-muted-foreground">
              <p>
                Last updated by {credentialsResponse.data.updatedBy.firstName} {credentialsResponse.data.updatedBy.lastName} on{' '}
                {new Date(credentialsResponse.data.updatedAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectCredentialsTab;